import {
  IWebsiteBuilderTab,
  websiteBuilderGenerateTabDimensions,
  websiteBuilderGenerateTabDesignStyles,
} from "pages/secure/facility/websiteBuilder/editor/nodes/tab/websiteBuilderTab";
import { useActivePageDomNodeRefMapRegistration } from "../../../../hooks/activePageDomNodeRefMap/useActivePageDomNodeRefMapRegistration";
import { useWebsiteBuilderEditorContext } from "../../../../websiteBuilderEditorContext";
import React, { ReactNode } from "react";
import { IWebsiteBuilderTabset } from "../../../../nodes/container/websiteBuilderTabset";
import { TWebsiteBuilderSection } from "../../../../nodes/section/websiteBuilderSection";
import { useDrop } from "react-dnd";
import { TWebsiteBuilderDraggableNodeDropItemType } from "../../../../nodes/websiteBuilderNode";
import { IWebsiteBuilderDropSectionEntityInFreeformDomainRect, IWebsiteBuilderFreeformParentContext, websiteBuilderDropSectionEntityInFreeform } from "../../../../nodes/websiteBuilderFreeformParentNode";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import classNames from "classnames";
import { isWebsiteBuilderStretchElementData, TWebsiteBuilderElement } from "../../../../nodes/element/websiteBuilderElement";

interface IWebsiteBuilderTabViewProps {
  node: IWebsiteBuilderTab;
  tabsetNode: IWebsiteBuilderTabset;
  sectionNode: TWebsiteBuilderSection;
  children: ReactNode;
}

interface IWebsiteBuilderTabViewDropState {
  tabIsDropTarget: boolean;
}

const ACCEPTED_TAB_DROP_ITEMS: TWebsiteBuilderDraggableNodeDropItemType[] = ["website-builder-element"];

export function WebsiteBuilderTabView(props: IWebsiteBuilderTabViewProps) {
  const { node, tabsetNode, sectionNode, children } = props;

  const dispatch = useAppDispatch();

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const selectedNode = useAppSelector(store => store.websiteBuilderStore.editor.selectedNode);

  const { activePageDomNodeRefMap, overlayLinesRef, tabsetActiveTabMap } = useWebsiteBuilderEditorContext();

  const activePageDomNodeRefMapRegistration = useActivePageDomNodeRefMapRegistration(node.id, activePageDomNodeRefMap);

  const designStyles = websiteBuilderGenerateTabDesignStyles();

  const active = tabsetActiveTabMap.get(tabsetNode.id) === node.id;

  const elementChildMoving =
    selectedNode?.type === "element" &&
    selectedNode.tabId === node.id &&
    selectedNode.editingMode.type === "element-move";

  const tabIsMoveTarget =
    selectedNode?.editingMode?.type === "element-move" &&
    selectedNode.editingMode.tabMoveTargetId === node.id;

  const [{ tabIsDropTarget }, drop] = useDrop<TWebsiteBuilderElement, unknown, IWebsiteBuilderTabViewDropState>(
    () => ({
      accept: ACCEPTED_TAB_DROP_ITEMS,
      drop: (item, monitor) => {
        if (monitor.didDrop()) {
          return;
        }

        const clientOffset = monitor.getClientOffset();
        
        const tabDomNode = activePageDomNodeRefMap.current.get(node.id);
        const tabBoundingRect = tabDomNode?.getBoundingClientRect();

        if (tabBoundingRect == null || clientOffset == null) {
          return;
        }

        const domainRect: IWebsiteBuilderDropSectionEntityInFreeformDomainRect = {
          left: tabBoundingRect.left,
          top: tabBoundingRect.top,
          width: tabBoundingRect.width,
          height: tabBoundingRect.height,
        };

        const parentContext: IWebsiteBuilderFreeformParentContext = {
          node,
          bounds: {
            desktop: websiteBuilderGenerateTabDimensions(tabsetNode, "desktop"),
            tablet: websiteBuilderGenerateTabDimensions(tabsetNode, "tablet"),
            mobile: websiteBuilderGenerateTabDimensions(tabsetNode, "mobile"),
          },
          ancestry: {
            sectionId: sectionNode.id,
            containerId: tabsetNode.id,
            gridCellId: null,
            tabId: node.id,
          },
        };

        websiteBuilderDropSectionEntityInFreeform(
          item,
          parentContext,
          domainRect,
          clientOffset,
          activePageId,
          deviceView,
          overlayLinesRef,
          dispatch,
        );
      },
      canDrop: item => {
        return (
          item.type === "element" && (
            !isWebsiteBuilderStretchElementData(item.element) ||
            !item.element.stretch.enabled
          )
        );
      },
      collect: (monitor): IWebsiteBuilderTabViewDropState => {
        return {
          tabIsDropTarget: monitor.canDrop() && monitor.isOver(),
        };
      },
    }),
    [
      node,
      tabsetNode,
      sectionNode.id,
      activePageId,
      deviceView,
    ],
  );

  function getTabHighlight() {
    return tabIsDropTarget || tabIsMoveTarget;
  }

  if (active) {
    return (
      <div
        ref={el => {
          drop(el);
          activePageDomNodeRefMapRegistration(el);
        }}
        className={classNames(designStyles.className, "editor-tab", {
          "editor-tab-highlight": getTabHighlight(),
        })}
        style={{
          ...designStyles.styleProperties,
          ...(elementChildMoving ? { position: "static" } : {}),
        }}
      >
        {children}
      </div>
    );
  } else {
    return null;
  }
}
